import { ApolloCache } from '@apollo/client';
import { Link, SharedFolder , FolderType } from 'types';

import { FormattedMessage } from 'react-intl';
import { ReactNode } from 'react';
import { MutationFunction } from 'types/utils/GraphQL';
import {
  GET_VISIBLE_FOLDERS_BY_FOLDER_TYPE_QUERY,
  GetVisibleFoldersFromFolderOutput,
  type ShareFolderMutationOutput,
  type UnshareFolderMutationOutput,
} from 'graphql/folder';
import { StateSetter } from 'types/utils/React';
import { trackEvent, GAEventCategory, GAShareEventActions } from 'services/googleAnalytics';
import {type ShowSnackbarFunction} from 'contexts/SnackbarContext';

type UpdateCacheInputType<T> = {
  cache: ApolloCache<T>;
  folder?: SharedFolder;
  selectedFileType?: FolderType;
  selectedLink?: Link;
};

export const updateCache = <T,>({
  cache,
  folder,
  selectedFileType,
  selectedLink,
}: UpdateCacheInputType<T>): void => {
  if (folder) {
    const variables = {
      getVisibleFoldersByFolderTypeInput: {
        channel: selectedLink?.channelKey,
        folderType: selectedFileType,
      },
    };

    const data =
      cache.readQuery<GetVisibleFoldersFromFolderOutput>({
        query: GET_VISIBLE_FOLDERS_BY_FOLDER_TYPE_QUERY,
        variables,
      });

    const updatedData = {
      ...data,
      visibleFoldersFromFolderType: {
        ...data?.visibleFoldersFromFolderType,
        folders: data?.visibleFoldersFromFolderType.folders.map((_) =>
          _.id === folder.id ? folder : _,
        ),
      },
    };

    cache.writeQuery({
      data: updatedData,
      query: GET_VISIBLE_FOLDERS_BY_FOLDER_TYPE_QUERY,
      variables,
    });
  }
};

type GetHandleValidateFunctionInputType = {
  addedContacts: string[];
  removedContacts: string[];
  selectedFolder?: SharedFolder;
  selectedLink?: Link;
  shareFolder: MutationFunction<ShareFolderMutationOutput>;
  handleErrorResult: () => void;
  setLoadingMessage: StateSetter<ReactNode | undefined>;
  setSelectedFolder: StateSetter<SharedFolder | undefined>;
  setShowEditContactsModal: StateSetter<boolean>;
  successSnackbar: ShowSnackbarFunction;
  unshareFolder: MutationFunction<UnshareFolderMutationOutput>;
};

export const getHandleValidateFunction =
  ({
    addedContacts,
    removedContacts,
    selectedFolder,
    selectedLink,
    shareFolder,
    handleErrorResult,
    setLoadingMessage,
    setSelectedFolder,
    setShowEditContactsModal,
    successSnackbar,
    unshareFolder,
  }: GetHandleValidateFunctionInputType) =>
  (): void => {
    const handleMutationError = (error: any) => {
      console.error(error);
      handleErrorResult();
    };

    setShowEditContactsModal(false);
    setLoadingMessage(<FormattedMessage id="share.document.add.loading" />);

    shareFolder({
      variables: {
        shareFolderInput: {
          channel: selectedLink?.channelKey,
          folderKey: selectedFolder?.key,
          otherWebUserIds: addedContacts,
        },
      },
    })
    .then(({ data: shareData } : { data : ShareFolderMutationOutput }) => {
      if (shareData?.shareFolder?.errorReason) {
        handleErrorResult();
      } else {
        trackEvent(
          GAEventCategory.SHARE,
          GAShareEventActions.EDIT_SHARED_FOLDER_ACCESS,
          `${addedContacts.length} contact(s) ajouté(s)`,
        );

        unshareFolder({
          variables: {
            unshareFolderInput: {
              channel: selectedLink?.channelKey,
              folderKey: selectedFolder?.key,
              otherWebUserIds: removedContacts,
            },
          },
        })
        .then(({ data: unShareData } : { data : UnshareFolderMutationOutput }) => {
          if (unShareData?.unshareFolder?.errorReason) {
            handleErrorResult();
          } else {
            trackEvent(
              GAEventCategory.SHARE,
              GAShareEventActions.EDIT_SHARED_FOLDER_ACCESS,
              `${removedContacts.length} contact(s) retiré(s)`,
            );

            successSnackbar(
              <FormattedMessage id="share.folder.share.success" />,
            );

            setSelectedFolder(unShareData?.unshareFolder.folder);
            setLoadingMessage(undefined);
          }
        })
        .catch(handleMutationError);
      }
    })
    .catch(handleMutationError);
  };
